import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./idahoCountiesMap.scss";

type IdahoCountiesMapProps = {
  setHoveredCounty: React.Dispatch<React.SetStateAction<string>>;
  hoveredCounty: string;
  activeCounty?: string;
};

const IdahoCountiesMap = ({
  setHoveredCounty,
  hoveredCounty,
  activeCounty,
}: IdahoCountiesMapProps) => {
  //eslint-disable-next-line
  const toolTipRef = useRef<any>(null);

  const touchDevice = "ontouchstart" in document.documentElement;
  const [isElementFocused, setIsElementFocused] = useState(false);
  const [tabIndex, setTabIndex] = useState(-1);

  useEffect(() => {
    if (hoveredCounty !== "" && !isElementFocused) {
      //eslint-disable-next-line
      toolTipRef.current.style.display = "block";
      onmousemove = (e: MouseEvent) => {
        toolTipRef.current.style.left = `${
          e.clientX - toolTipRef.current.offsetWidth / 2
        }px`;
        toolTipRef.current.style.top = `${
          e.clientY - (toolTipRef.current.offsetHeight + 10)
        }px`;
        setIsElementFocused(false);
      };
    } else {
      onmousemove = null;
    }
    return () => {
      onmousemove = null;
      setIsElementFocused(false);
    };
  }, [hoveredCounty]);

  const handleFocus = (
    e: React.FocusEvent<SVGPathElement | HTMLAnchorElement>,
    county: string
  ) => {
    toolTipRef.current.style.display = "block";
    setIsElementFocused(true);
    setHoveredCounty(county);

    if (toolTipRef.current) {
      const rect = e.target.getBoundingClientRect();
      toolTipRef.current.style.left = `${rect.left}px`;
      toolTipRef.current.style.top = `${rect.top - 30}px`;
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (toolTipRef.current) {
        toolTipRef.current.style.display = "none";
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <div
        ref={toolTipRef}
        className={hoveredCounty && !touchDevice ? "tooltip" : "tooltip hide"}
      >
        <p className="tooltip__title">{hoveredCounty + " \nCounty"}</p>
        <p className="tooltip__text">CLICK TO VIEW</p>
      </div>
      <svg
        className="idaho-container"
        id="Layer_1"
        data-name="Layer 1"
        viewBox="0 0 608.47 907.7"
        aria-label="This is Idaho's map. To visit the counties, press Enter."
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            setTabIndex(0);
          }
          if (e.key === "Escape") {
            setTabIndex(-1);
            const activeEl = document.activeElement as HTMLElement | SVGElement;
            if (activeEl?.blur) {
              activeEl.blur();
            }
          }
        }}
      >
        <Link
          to="/Counties/542"
          onFocus={(e) => handleFocus(e, "Idaho")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Idaho County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty(() => "Idaho")}
            className={
              activeCounty === "Idaho"
                ? "active-county-section"
                : "county-section"
            }
            d="M48.58,404.25c2.29-1.65,2.9-3.79,3.01-6.24,.2-4.53,.97-8.91,4.81-12.11,.48-.4,1.26-.7,1.88-.66,3.3,.22,3.57,.08,4.6-2.97,.2-.58,.54-1.12,1.02-2.07,.83,.93,1.48,1.54,1.98,2.24,.97,1.35,5.2,3.22,6.85,2.82,1.36-.33,2.5-1.01,2.44-2.64-.07-2.19-.1-4.39-.2-6.58-.05-1.05,.15-1.79,1.31-2.29,2.2-.96,3.27-2.79,3.6-5.04,.26-1.72,.43-3.46,.8-5.16,.29-1.34,1.04-2.31,2.74-2.34,1.4-.02,2.8-.39,4.21-.48,2.66-.16,4.81,.76,6.56,2.77,.71,.81,1.83,1.55,2.89,1.78,2.41,.52,4.9,.92,7.36,.95,2.61,.03,3.56-1.11,3.75-3.58,.15-1.89,3.46-4.24,5.42-3.88,3.3,.62,6.6,1.26,9.92,1.8,1.77,.28,2.05-.18,1.49-1.78-.89-2.56-2.97-4.05-5.35-4.96-4.97-1.91-6.51-5.41-5.82-10.2,.11-.75,.02-1.52,.02-2.25,1.66-.57,2.77-.02,3.48,1.11,3.07,4.88,7.88,6.99,13.45,8.03,2.51,.47,4.98,1.13,7.47,1.68,3.4,.76,5.74-.21,7.5-3.1,3.67-6.02,7.37-12.02,10.99-18.06,1.72-2.87,4.2-4.59,7.56-5.31,18.97-4.11,37.93-8.21,56.88-12.4,9.5-2.1,18.93-4.51,28.46-6.51,3.72-.78,7.63-.67,11.42-1.18,3.86-.52,7.69-1.28,11.53-1.96,4.5-.79,6.42,1.01,5.23,5.22-1.65,5.84-3.89,11.56-5.19,17.47-2.08,9.4-3.61,18.91-5.34,28.38-.2,1.08-.2,2.19-.25,3.28-.18,3.5-1.3,6.5-4.25,8.85-2.34,1.87-2.48,3.41-.57,5.7,2.19,2.62,2.65,5.37,1.61,8.52-1.01,3.05-.2,5.72,2.14,7.99,3.58,3.45,5.26,7.73,5.85,12.47,.19,1.5-.33,2.65-1.93,3.21-1.96,.69-3.93,1.35-5.83,2.17-6.75,2.89-8.78,8.86-4.77,14.81,2.66,3.94,2.78,7.61,.24,11.56-1.56,2.42-2.82,4.99-2.66,8,.03,.5-.31,1.12-.68,1.52-3.04,3.37-6.08,6.74-9.23,10.01-.51,.53-1.59,.82-2.38,.77-2.21-.16-4.39-.69-6.6-.82-3.24-.18-4.69,1.78-3.21,4.59,1.78,3.38,3.7,6.75,6.01,9.81,2.43,3.24,5.38,6.12,8.12,9.14,2.12,2.34,2.28,3.69,.27,6.17-2.35,2.91-4.85,5.7-7.35,8.5-.32,.35-1.02,.55-1.55,.54-22.84-.24-45.67-.45-68.51-.81-14.34-.23-28.68-.78-43.02-1.04-6.42-.12-12.46,1.6-17.73,5.07-2.25,1.48-3.97,3.76-5.72,5.83-.68,.81-1.05,1.29-2.21,.91-.93-.31-1.98-.33-2.99-.39-4.71-.3-7.16-2.58-7.74-7.1-.3-2.3-.58-4.63-1.25-6.85-1.59-5.29-4.76-7.37-10.5-7.62-7-.3-13.98-.8-20.97-1.23-.39-.02-.77-.15-1.43-.28,1.12-3.41,2-6.77,3.31-9.98,3.75-9.13,7.71-18.17,13.73-26.23,2.13-2.84,2.05-6.11,.68-9.26-1.55-3.57-3.23-7.09-4.97-10.58-2.54-5.11-6.36-8.85-12.16-10.63-4.08-1.25-7.14-4.02-10.23-7.11Z"
          />
        </Link>
        <Link
          to="/Counties/554"
          onFocus={(e) => handleFocus(e, "Owyhee")}
          onBlur={() => setHoveredCounty("")}
          tabIndex={tabIndex}
          aria-label="Owyhee County"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Owyhee")}
            className={
              activeCounty === "Owyhee"
                ? "active-county-section"
                : "county-section"
            }
            d="M208.78,806.27c.31,32.91,.62,66.08,.93,99.42-1.33,0-2.52,.01-3.71,0-19.53-.26-39.06-.51-58.59-.78-11.18-.16-22.36-.28-33.54-.53-16.42-.38-32.85-.87-49.27-1.29-12.32-.31-24.64-.56-36.96-.9-4.04-.11-8.07-.43-12.1-.63-3.12-.16-4.21-1.06-4.09-4.04,.25-6.45,.74-12.89,1-19.34,.29-7.29,.51-14.58,.64-21.88,.21-12.59,.25-25.19,.51-37.78,.48-23.04,1-46.09,1.63-69.13,.42-15.62,.99-31.23,1.63-46.84,.21-5.05,.88-10.09,1.38-15.53,1.19,.48,1.87,.6,2.34,.97,4.51,3.59,9.05,7.13,13.43,10.86,2.83,2.41,4.55,5.58,5.38,9.13,.65,2.76,1.92,5.18,3.84,7.36,5.36,6.06,10.62,12.19,15.99,18.25,.65,.74,1.64,1.35,2.59,1.66,3.37,1.11,6.8,2.04,10.19,3.09,2.84,.88,4.55,2.56,5.01,5.59,.6,3.97,1.76,7.82,4.56,11.01,2.87,3.26,6.58,5.29,10.68,6.71,1.17,.4,1.44,1.01,1.78,2.13,.53,1.78,1.34,3.78,2.71,4.97,4.4,3.8,9.05,7.34,13.71,10.86,3.63,2.75,5.7,2.5,8.89-.73,2.12-2.15,4.19-2.34,7.55-1.18,5.56,1.92,11.12,3.95,16.84,5.34,3.93,.95,8.13,.92,12.21,1.29,3.22,.29,6.45,.41,9.66,.79,2.95,.34,3.82,1.43,3.61,4.31-.23,3.22-.63,6.43-.92,9.64-.38,4.25,1.37,6.67,5.76,7.06,4.14,.37,8.33,.16,12.5,.16,7.33,.02,14.67,0,22.23,0Z"
          />
        </Link>
        <Link
          to="/Counties/536"
          onFocus={(e) => handleFocus(e, "Custer")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Custer County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Custer")}
            className={
              activeCounty === "Custer"
                ? "active-county-section"
                : "county-section"
            }
            d="M216.98,649.78c-.34-3.39-1.8-5.8-4.39-7.65-1.16-.83-1.53-1.79-.69-3.3,3.34-6.02,1.51-12.16-4.52-15.75-.69-.41-1.39-.81-2.12-1.12-3.84-1.66-5.74-4.41-5.74-8.52,0-3.1-2.28-4.77-4.88-6.12-1.71-.89-3.43-1.79-4.99-2.89-1.06-.74-1.85-1.84-2.82-2.84,.35-.36,.54-.62,.78-.81,6.88-5.31,8.16-13.32,3.25-20.35-.97-1.39-2.11-2.72-2.82-4.23-.65-1.37-1.07-2.93-1.14-4.43-.1-2.06,1.03-3.53,3.19-4.18,6.97-2.09,10.66-6.86,11.93-13.57,.12-.64,.21-1.28,.38-1.9,.87-3.19,3.83-4.32,6.73-2.57,.57,.34,1.07,.78,1.6,1.18,4.11,3.11,7.31,3.17,11.52,.11,2.74-1.99,5.42-4.05,8.13-6.07,.36-.27,.73-.51,1.33-.93,.23,1.26,.54,2.31,.6,3.37,.31,5.03,2.8,8.99,6.7,12.13,1.29,1.04,3,2,4.62,2.19,4.92,.6,8.15,3.5,10.49,7.15,1.64,2.56,3.47,3.23,6.12,2.37,2.14-.7,4.37-1.44,6.2-2.65,3.75-2.48,6.07-6.17,8.04-10.02,.99-1.94,.93-3.98,.12-6.02-.2-.51-.21-1.32,.08-1.73,3.28-4.69,6.91-9.1,11.82-12.34,1.01-.67,2.4-.81,3.61-1.2,.39,1.37,1.08,2.74,1.11,4.12,.05,1.85-.63,3.71-.6,5.56,.05,3.51,2.5,5.44,6.13,5,1.39-.17,2.8-.47,4.11-.94,1.14-.41,1.84-.1,2.37,.77,.74,1.22,1.42,2.48,2.07,3.75,7.95,15.43,19.57,27.09,36.9,32.8,3.43,1.13,6.98,1.9,10.51,2.74,3.02,.71,6.03,.53,8.9-.7,1.32-.57,2.28-.12,2.88,.97,3.9,6.99,8.38,13.71,11.01,21.3,.06,.18,.06,.38,.12,.76-2.04-.1-4.03-.1-5.99-.32-1.95-.22-2.68,.49-2.63,2.33,.08,2.95,.22,5.92-.02,8.86-.43,5.2-1.25,10.36-1.68,15.56-.5,6.11-1.09,12.24-.93,18.34,.06,2.23,2.14,4.44,3.44,6.59,.21,.34,1.15,.21,1.66,.47,1.21,.62,2.36,1.34,3.49,2-2.39,1.43-4.41,2.49-6.24,3.8-.99,.71-1.79,1.76-2.42,2.8-2.01,3.32-4.74,5.89-8.1,7.95-5.79,3.55-11.57,7.11-17.21,10.86-2.45,1.63-4.56,3.75-7.24,5.99-1.88-1.71-4.09-3.48-6-5.5-4.93-5.21-9.77-10.51-14.57-15.84-1.19-1.32-2.1-1.63-3.67-.43-3.51,2.66-5.42,2.62-9.12,.23-4.63-2.99-8.61-6.64-11.82-11.02-3.2-4.36-7.48-6.48-13.08-6.36-1.86,.04-3.76-.29-5.6-.64-3.07-.59-5.36,.31-7.13,2.8-2.68,3.77-2.72,3.72-5.78,.37-3.05-3.34-7.13-5.23-11.42-6.25-3.82-.91-5.94-2.69-6.57-6.33-.22-1.26-.71-2.49-1.14-3.71-1.53-4.34-4.67-5.55-8.79-3.23-1.66,.94-3.07,2.29-4.57,3.48-.94,.73-1.81,1.55-2.78,2.23-.75,.53-1.6,.91-2.71,1.52Z"
          />
        </Link>
        <Link
          to="/Counties/547"
          onFocus={(e) => handleFocus(e, "Lemhi")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Lemhi County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Lemhi")}
            className={
              activeCounty === "Lemhi"
                ? "active-county-section"
                : "county-section"
            }
            d="M424.84,600.64c.21,1.62,.55,3.24,.61,4.87,.06,1.67-.14,3.35-.24,5.03-.15,2.42-1.14,3.58-3.72,3.73-5.51,.31-11.04,.72-16.55,.58-8.54-.22-17.07-.88-25.61-1.31-1.16-.06-1.8-.48-2.17-1.56-2.61-7.67-7.08-14.46-11.05-21.49-1.23-2.19-2.86-2.76-5.2-1.82-2.8,1.13-5.61,1.06-8.52,.51-20.22-3.84-34.61-15.05-44.35-32.16-1.06-1.86-1.78-3.91-2.91-5.73-1.59-2.57-2.31-2.73-5.3-1.9-.71,.2-1.42,.41-2.14,.57-3.7,.83-5.79-.92-5.4-4.54,.1-.96,.4-1.9,.5-2.86,.12-1.21,.32-2.46,.16-3.66-.44-3.37-2.83-4.68-5.78-2.89-2.65,1.6-5.17,3.53-7.33,5.69-2.5,2.51-4.62,5.38-6.72,8.22-.5,.67-.59,1.98-.26,2.77,.78,1.89,.97,3.76-.16,5.39-2,2.89-4.08,5.76-6.5,8.33-1.19,1.26-3.1,2.05-4.84,2.65-3.45,1.18-3.83,.84-6.06-1.91-2.94-3.63-6.31-6.59-11.44-7.33-1.81-.26-3.66-1.63-5.04-2.93-2.87-2.68-4.22-6.08-4.93-9.94-.61-3.28,.11-5.55,2.3-8.1,4.22-4.91,5.57-10.9,5.66-17.21,.13-8.85,.32-17.69,.65-26.53,.05-1.36,.76-2.86,1.61-3.98,1.9-2.5,4.16-4.74,6.15-7.18,2.91-3.58,2.75-5.72-.34-9.08-4.8-5.23-9.99-10.19-12.9-16.75-.24-.53-.54-1.04-.75-1.59-.6-1.57-.15-2.28,1.59-2.13,2.21,.19,4.41,.52,6.58,.96,1.55,.31,2.65,.09,3.72-1.14,2.85-3.27,5.87-6.4,8.76-9.65,.78-.88,1.44-.97,2.55-.56,5.5,2.06,9.74,5.66,13.53,9.86,2.06,2.29,4.57,3.79,7.81,2.75,1.4-.45,2.81-1.36,3.78-2.43,2.99-3.3,6.38-5.99,10.37-8.19,5.96-3.28,10.27-8.25,13.77-13.89,2.93-4.72,3.83-4.77,7.89-.92,3.21,3.04,6.48,6.01,9.69,9.05,.34,.32,.57,.84,.64,1.3,.1,.63,.15,1.33-.04,1.93-1.08,3.49-.47,6.25,3.06,8.26,.82,.47,1.41,2.01,1.4,3.04-.11,7.42,2.31,14.33,5.23,20.93,5.28,11.94,12.27,22.94,21.94,32.22,1.46,1.4,2.79,3.06,3.64,4.84,.65,1.36,.62,3.1,.55,4.65-.05,1.05-.51,2.17-1.08,3.1-2.44,4-1.76,7.78,1.11,11.14,2.89,3.38,6.01,6.61,9.29,9.64,2.33,2.15,5.42,2.07,8.4,1.31,5.09-1.3,6.29-.99,9.47,3.08,3.27,4.19,6.43,8.45,9.65,12.68,1.32,1.72,1.65,3.43,.97,5.61-.56,1.77-.8,4-.17,5.68,2.16,5.75,4.56,11.43,7.27,16.96,1.63,3.33,4.58,5.75,8.15,7.25,1.99,.84,3.21,2.2,3.99,4.1,1.13,2.74,1.93,3.15,5.06,2.72Z"
          />
        </Link>
        <Link
          to="/Counties/560"
          onFocus={(e) => handleFocus(e, "Valley")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Valley County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Valley")}
            className={
              activeCounty === "Valley"
                ? "active-county-section"
                : "county-section"
            }
            d="M240.21,493.22c0,4.08,.02,7.94,0,11.81-.03,5.62-.12,11.24-.12,16.86,0,8.61-3.43,15.69-10.34,21.17-2.9,2.31-5.9,4.51-8.92,6.68-3.25,2.33-5.61,2.24-8.78-.14-.37-.28-.72-.58-1.1-.86-4.5-3.33-9.38-1.56-10.47,3.77-.26,1.26-.49,2.54-.87,3.77-1.42,4.66-4.53,7.79-9.31,9.46-4.8,1.68-6.49,5.03-4.55,9.62,.94,2.24,2.5,4.26,3.86,6.32,3.3,4.98,3.27,10.67-.19,15.04-1.77,2.23-3.78,3.83-7.13,3.46-3.11-.34-4.7,1.3-4.27,4.37,.29,2.08-.55,3.29-2.02,4.46-4.25,3.38-9.04,5.19-14.64,5.13-15.96-.18-31.93-.27-47.89-.37-.93,0-1.88,.12-2.8,.29-3.13,.57-4.39,1.92-4.71,4.93-.39,3.75-3.77,5.32-6.94,3.19-.34-.23-.53-.86-.55-1.31-.17-4.32-.34-8.64-.39-12.97-.02-1.82,.62-3.41,2.58-4.24,2.21-.93,3.32-2.67,3.22-4.93-.24-5.73-.68-11.46-.88-17.19-.12-3.47,.2-6.95,.07-10.42-.35-9.35-.61-18.71-1.34-28.03-.5-6.41,1.31-12.26,3.45-18.11,1.25-3.4,2.63-6.77,3.92-10.16,1.94-5.1,1.1-8.75-2.88-12.18,1.18-3.24,3.63-5.42,6.66-7.1,6.14-3.4,12.79-4.54,19.81-4.25,8.61,.36,17.22,.74,25.84,.86,14.15,.2,28.3,.14,42.44,.31,12.66,.15,25.31,.45,37.97,.69,.53,.01,1.06,.07,1.26,.08Z"
          />
        </Link>
        <Link
          to="/Counties/537"
          onFocus={(e) => handleFocus(e, "Elmore")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Elmore County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Elmore")}
            className={
              activeCounty === "Elmore"
                ? "active-county-section"
                : "county-section"
            }
            d="M215.26,652.42c-1.22,3.46-.87,6.6-.05,9.79,.23,.91-.13,2.09-.56,3-1.03,2.16-2.38,4.19-3.38,6.36-1.33,2.86-1.59,5.84-.49,8.87,1.41,3.87,.73,7.47-1.44,10.93-3.46,5.5-4.23,11.65-4.3,17.9-.27,25.64-.47,51.28-.69,76.92-.02,2.49,0,2.49,2.65,2.52,.53,0,1.06,0,1.82,0v15.63c-2.48,0-4.94-.03-7.39,0-8.15,.12-16.29,.26-24.44,.39-.27,0-.54,0-.81,0-5.11-.19-6.48-1.59-6.13-6.45,.2-2.83,.63-5.65,.81-8.49,.29-4.38-1.29-6.12-5.95-6.51-4.02-.34-8.08-.35-12.08-.83-4.45-.54-8.92-1.19-13.24-2.29-4.27-1.08-8.39-2.76-12.55-4.23-3.6-1.27-6.54-.67-9.16,1.98-2.32,2.35-3.52,2.5-6.22,.44-4.58-3.5-9.1-7.08-13.62-10.66-1.64-1.3-2.6-2.94-2.38-5.05,3.89-1.72,7.93-2.39,12.17-2.14,1.67,.1,3.37,0,5.04-.13,3.15-.24,4.64-1.57,4.93-4.61,.36-3.79,.72-7.59,.77-11.4,.11-8.27-.05-16.54,.07-24.81,.03-2.3,.81-4.58,.93-6.89,.14-2.49-.05-5.01-.16-7.51-.08-1.85-.27-3.7-.43-5.66,3.18-.54,6.11-.9,8.96-1.57,2-.46,4.07-1.06,5.81-2.07,4.94-2.88,9.76-5.97,14.57-9.05,2.83-1.82,4.8-4.27,5.51-7.58,.58-2.71,2.07-4.84,4.65-6.26,2.29-1.26,3.6-3.28,4.18-5.7,.99-4.17,3.67-6.84,7.85-8.07,2.76-.81,5.61-1.33,8.42-2,8.13-1.92,11.22-5.36,12.13-13.39,.19-1.66,.46-3.33,.88-4.95,.2-.76,.7-1.6,1.33-2.07,1.91-1.41,3.58-.97,4.6,1.24,.82,1.76,1.33,3.66,2.07,5.45,.69,1.67,1.38,3.37,2.32,4.93,2.5,4.15,5.6,5.43,10.52,4.52,.72-.13,1.43-.3,2.48-.52Z"
          />
        </Link>
        <Link
          to="/Counties/524"
          onFocus={(e) => handleFocus(e, "Blaine")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Blaine County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Blaine")}
            className={
              activeCounty === "Blaine"
                ? "active-county-section"
                : "county-section"
            }
            d="M275.2,748.59c0-4.17-.09-8.23,.04-12.27,.06-1.93-.5-2.88-2.65-2.94-2.15-.06-4.3-.34-6.43-.61-2.52-.32-3.04-1.02-2.92-3.46,.05-.96,.11-1.97-.12-2.89-1.01-4-2-7.95-1.83-12.16,.06-1.42-1.27-3.1-2.37-4.32-3.05-3.36-6.3-6.55-9.5-9.78-1.6-1.62-2.39-3.52-2.54-5.75-.38-5.8-.78-11.6-1.23-17.39-.31-3.93-2.38-5.71-6.38-5.56-4.27,.15-8.31-.76-12.1-2.57-3.07-1.46-6.05-3.11-9.06-4.68-.23-.12-.5-.24-.63-.44-1.97-2.87-1.68-9.42,.52-12.24,.12-.15,.19-.36,.34-.47,2.95-2.23,5.84-4.55,8.89-6.64,3.64-2.5,5.82-1.66,7.15,2.48,.77,2.4,1.5,4.83,2.51,7.14,.37,.85,1.37,1.67,2.28,2.04,2.65,1.08,5.45,1.86,8.12,2.9,2.91,1.13,5.36,2.88,7.37,5.26,2.43,2.89,4.25,2.77,6.57-.24,.56-.73,.97-1.68,1.71-2.13,1.11-.67,2.46-1.4,3.7-1.38,2.94,.04,5.88,.58,8.83,.78,4,.27,6.99,2.14,9.29,5.15,3.09,4.04,6.58,7.65,10.82,10.64,5.93,4.18,7.4,4.32,13.78,.64,3.99,4.29,8.01,8.62,12.03,12.95,1.97,2.12,3.82,4.36,5.95,6.32,3.36,3.08,7.19,5.23,12.16,4.44,1.56-.25,2.54,.5,2.56,1.99,.03,1.7,.98,2.81,2.02,4.03,1.24,1.44,2.27,3.04,3.31,4.62,1.06,1.61,1.18,3.31,.75,5.2-.48,2.13-.71,4.34-.81,6.52-.11,2.36,1.35,4.03,3.84,4.15,4.76,.23,9.54,.21,14.31,.29,.54,0,1.07-.09,1.61-.11,4.48-.2,5.62,.74,6.1,5.03,.56,5.04,2.36,6.47,7.66,6.19,6.99-.37,13.98-.78,20.98-1.02,2.24-.08,4.5,.32,6.46,.48,0,6.21-.02,12.01,.01,17.81,.01,1.73-.96,2.09-2.53,2.16-4.3,.19-8.6,.49-12.9,.75-4.35,.26-6.92,2.73-6.81,6.83,.3,11.1,.61,22.2,1.1,33.3,.19,4.26,1.78,5.93,6.07,6.79,1.96,.39,2.71,1.13,2.5,3.04-.13,1.14,.15,2.31,.22,3.47,.08,1.35-.47,2.27-1.95,2.47-3.07,.41-5,2.13-5.93,4.87-.37,1.07-.92,1.29-2.05,1.26-2.87-.07-5.23-1.03-7.15-3.06-.68-.72-1.44-1.36-2.14-2.06-1.43-1.45-2.94-2.44-5.28-2.2-1.76,.18-3.58-.29-5.6-.49,0-5.02-.02-9.9,.03-14.77,0-.46,.35-1,.7-1.35,3.78-3.83,4.84-8.47,4.72-13.57-.21-8.39-.29-16.79-.43-25.19-.02-1.48-.07-2.97-.14-4.45-.17-3.69-1.79-5.21-5.7-5.27-4.11-.06-8.22-.07-12.33-.07-23.99,0-47.99,0-71.98-.02-1.73,0-3.46-.25-5.5-.41Z"
          />
        </Link>
        <Link
          to="/Counties/557"
          onFocus={(e) => handleFocus(e, "Shoshone")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Shoshone County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Shoshone")}
            className={
              activeCounty === "Shoshone"
                ? "active-county-section"
                : "county-section"
            }
            d="M216.13,266.77c-1.12,.12-2.23,.33-3.35,.33-20.55-.02-41.1,.23-61.64-.22-14.12-.31-28.21-1.61-42.32-2.44-3.96-.23-7.93-.5-11.9-.5-1.68,0-2.47-.51-2.83-1.98-.32-1.32-.75-2.61-1.07-3.93-.1-.41-.16-.95,.04-1.28,2.42-3.95,2.27-8.29,2.21-12.62-.13-9.3-.33-18.6-.36-27.9,0-2.28,1.01-4.65,.64-6.83-.56-3.27-.15-6.33,.32-9.53,.51-3.5,.46-7.08,.47-10.63,.04-14.21-.11-28.43,.08-42.63,.08-5.78,.76-11.56,1.33-17.33,.11-1.16,.75-2.36,1.44-3.35,2.19-3.12,3.36-3.37,6.89-1.75,4.43,2.03,8.86,4.06,13.26,6.14,.55,.26,1.13,.74,1.4,1.26,3.04,5.95,7.11,11.23,11.33,16.44,6.95,8.58,12.96,17.77,18.27,27.37,2.5,4.53,2.27,7.38-1.31,11.3-1.34,1.47-1.32,2.49-.08,3.99,2.84,3.44,5.49,7.02,8.38,10.75-1.28,.56-2.41,1.06-3.55,1.55-1.83,.79-3.1,1.97-3.36,4.01-.26,2.06,1.12,3.25,2.64,3.99,6.24,3.03,11.2,7.54,16.06,12.23,2.53,2.45,5.86,3.57,9.34,4.31,1.9,.4,3.89,.76,5.57,1.61,1.54,.78,3.36,1.97,3.93,3.39,1.92,4.77,5.71,7.89,9.43,11.16,4.07,3.56,7.6,7.62,9.59,12.55,1.41,3.51,3.98,5.7,7.01,7.7,.92,.61,1.63,1.51,2.43,2.28-.1,.18-.2,.36-.29,.55Z"
          />
        </Link>
        <Link
          to="/Counties/533"
          onFocus={(e) => handleFocus(e, "Cassia")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Cassia County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Cassia")}
            className={
              activeCounty === "Cassia"
                ? "active-county-section"
                : "county-section"
            }
            d="M412.67,904.27c-41.75,.44-83.3,.87-125.33,1.31,0-1.94,.03-3.85,0-5.76-.24-14.98-.51-29.96-.74-44.94-.05-2.95,0-3.01,3.16-2.95,3.5,.06,7,.25,10.5,.36,3.58,.11,6.02-2.16,6.08-5.66,.03-1.48-.02-2.97,.02-4.45,.05-1.74,.93-2.87,2.69-3.45,1.91-.62,3.77-1.37,5.65-2.08,2.67-1.01,5.22-1.94,8.32-1.17,2.13,.53,4.57,.28,6.82,0,3.11-.39,5.83,.7,8.64,1.54,.24,.07,.54,.21,.66,.41,1.64,2.6,3.72,1.82,6.14,1.06,6.7-2.1,8.2-7.25,8.7-13.02,.19-2.18,.05-4.38-.03-6.58-.07-1.81,.78-2.88,2.62-3.01,3.26-.23,6.53-.43,9.8-.45,2.14-.02,4.29,.25,6.42,.48,.55,.06,1.13,.37,1.57,.71,.84,.65,1.67,1.33,2.35,2.12,2.77,3.25,6.32,4.65,10.67,4.46,.8-.03,1.75,.25,2.41,.69,5.22,3.5,10.79,5.79,17.34,5.16,1.58-.15,1.95,.92,2.33,2.14,1.2,3.85,.79,7.79,.92,11.71,.24,7.35,1.22,14.72,.95,22.05-.47,12.75,.04,25.43,1.35,38.1,.03,.31,0,.63,0,1.22Z"
          />
        </Link>
        <Link
          to="/Counties/535"
          onFocus={(e) => handleFocus(e, "Clearwater")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Clearwater County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Clearwater")}
            className={
              activeCounty === "Clearwater"
                ? "active-county-section"
                : "county-section"
            }
            d="M94.92,265.26c6.1,.4,11.59,.8,17.09,1.12,12.9,.76,25.79,1.77,38.7,2.15,14.81,.43,29.63,.32,44.45,.39,5.93,.03,11.85-.07,17.78-.12,.2,0,.42-.05,.6,0,1.48,.38,3.06-1.49,4.48,.18,2.59,3.03,5.98,5.91,5.1,10.27-.76,3.76,.34,6.26,3.6,8.15,1.73,1,3.41,2.06,5.15,3.04,2.62,1.47,4.31,3.38,4.21,6.53-.03,.94,.61,1.93,1.09,2.83,.67,1.27,1.61,1.32,2.8,.48,2.41-1.72,4.88-3.35,7.23-4.95,2.28,1.69,2.14,4.62,2.99,9.71-5.23,1.24-10.47,2.56-15.75,3.73-20.97,4.66-41.94,9.28-62.92,13.89-2.69,.59-5.41,1.02-8.1,1.58-3.48,.72-6.11,2.6-7.91,5.54-3.65,5.95-7.3,11.9-10.92,17.87-1.88,3.1-3.04,3.6-6.6,2.76-3.2-.75-6.41-1.47-9.61-2.22-3.82-.89-6.93-2.77-9.07-6-1.2-1.81-2.75-2.92-5.17-2.71-.65,.06-1.57-.42-2.03-.94-3.03-3.34-6.78-4.63-11.3-3.95-.2,.03-.41-.02-.61,0-2.59,.43-4.29-.57-5.88-2.61-1.7-2.18-4-3.93-6.33-5.59-.04-.12-.14-.26-.11-.36,1.05-3.8,.3-4.64-3.85-4.36-.79,.05-1.59,0-2.59,0v-13.06c3.5,0,7.06-.14,10.59,.06,1.81,.1,2.22-.44,2.17-2.01-.25-8.26-.63-16.52-.56-24.78,.05-5.4,.79-10.79,1.26-16.63Z"
          />
        </Link>
        <Link
          to="/Counties/529"
          onFocus={(e) => handleFocus(e, "Butte")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Butte County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Butte")}
            className={
              activeCounty === "Butte"
                ? "active-county-section"
                : "county-section"
            }
            d="M368.75,614.48c4.64,.31,8.99,.65,13.35,.88,7.8,.42,15.59,.87,23.39,1.09,2,.06,2.54,.69,2.57,2.42,.14,7.36,.41,14.71,.51,22.07,.09,6.4,2.39,8.71,9.15,8.79,5.79,.07,11.59,.09,17.38,.14,.72,0,1.44,.13,2.27,.21v7.51c0,9.69-.03,19.38,.03,29.08,0,1.18,.58,2.35,.73,3.54,.22,1.69,.52,3.42,.37,5.09-.18,1.98-.48,4.06-1.34,5.82-.55,1.13-2.16,1.83-3.4,2.57-.53,.32-1.31,.22-1.96,.36-3.48,.74-4.59,1.98-4.9,5.43-.1,1.16-.19,2.31-.28,3.47-.14,1.83-1.26,2.76-3.08,2.53-5.93-.72-8.54,1.72-8.65,8.04-.04,2.51-.21,5.04-.62,7.52-.24,1.41-.92,2.82-1.72,4.05-1.16,1.79-2.69,2.35-5.21,2.08-4.91-.53-9.93-.34-14.9-.24-3.97,.08-7.93,.47-11.89,.69-4.15,.23-5.15-.58-5.62-4.5-.05-.45-.12-.9-.19-1.34-.6-3.8-2.49-5.42-6.55-5.37-3.56,.04-7.12,.32-10.68,.34-2.01,0-4.02-.34-6.04-.45-1.72-.1-2.45-1.11-2.38-2.55,.09-2.05,.29-4.13,.7-6.14,.44-2.18,.45-4.27-.79-6.17-1.28-1.96-2.77-3.8-4.11-5.73-.42-.61-.85-1.3-.97-2-.54-3.21-1.57-4.03-4.98-3.94-1.55,.04-3.11-.12-4.66-.19-.09-.24-.19-.47-.28-.71,1.63-1.47,3.1-3.18,4.93-4.38,6.11-4.01,12.33-7.86,18.55-11.71,3.41-2.11,6.13-4.79,8.21-8.13,1.56-2.5,3.6-4.41,6.75-5.09,2.93-.64,3.21-1.39,1.39-3.64-.74-.92-1.82-1.77-2.93-2.19-2.93-1.1-5-3.72-5.06-6.79-.16-7.58,.55-15.09,2.06-22.53,.89-4.4,1.18-8.79,.45-13.28-.33-2.04,.22-4.22,.39-6.64Z"
          />
        </Link>
        <Link
          to="/Counties/523"
          onFocus={(e) => handleFocus(e, "Bingham")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Bingham County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Bingham")}
            className={
              activeCounty === "Bingham"
                ? "active-county-section"
                : "county-section"
            }
            d="M410.36,793.03c0-3.06,.07-5.83-.01-8.6-.24-7.74-.55-15.48-.83-23.21-.02-.45,0-.9,.02-1.36,.27-6.31,.52-12.63,.87-18.94,.04-.79,.39-1.85,.99-2.28,3.24-2.29,4.72-5.37,4.93-9.13,.14-2.51,.3-5.03,.45-7.54,.21-3.62,1.94-5.08,5.74-4.83,4.17,.27,5.72-1.01,5.99-4.96,.02-.32,.04-.64,.06-.97,.21-4.66,.73-5.2,5.6-5.8,2.48-.31,4.48-2.31,4.9-4.61,.4-2.22,1.04-4.39,1.58-6.65h15.41c0,4.36,.02,8.73-.01,13.1-.01,1.68-.16,3.35-.24,5.03-.23,5.33,3,8.23,8.52,7.74,2.94-.26,5.9-.42,8.85-.46,6.33-.1,12.67-.1,19-.15,2.78-.02,5.28,.62,7.52,2.32,5.03,3.8,10.82,5.05,17.11,4.24,4.89-.63,6.5,.42,7.54,5.02,.85,3.71,2.01,4.76,5.96,5.06,3.62,.27,7.26,.43,10.89,.55,4.98,.18,6.59,1.57,6.64,6.29,.08,7.94,.02,15.88,.02,24.08-3.67,.37-7.24,.86-10.84,1.08-11.02,.69-22.04,1.3-33.07,1.87-2.42,.12-4.85-.12-7.27-.09-16.3,.19-32.61,.46-48.91,.59-4.28,.03-7.41,1.77-8.96,5.42-2.42,5.69-6.56,10.17-10.35,14.94-.93,1.16-1.91,1.7-3.53,1.72-4.24,.03-8.48,.33-12.71,.52-.53,.02-1.05,0-1.86,0Z"
          />
        </Link>
        <Link
          to="/Counties/559"
          onFocus={(e) => handleFocus(e, "Twin Falls")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Twin Falls County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Twin Falls")}
            className={
              activeCounty === "Twin Falls"
                ? "active-county-section"
                : "county-section"
            }
            d="M210.46,788.88c3.16-.25,5.83-.46,8.74-.69,1.46,3.66,2.29,7.42,.8,11.34-1.45,3.84-.64,5.67,3.28,7.01,2.7,.92,4.26,2.79,5.05,5.38,1.68,5.5,4.77,7.63,10.71,8.26,5.72,.61,11.35,1.54,16.84,3.62,8.55,3.25,17.37,5.84,26.12,8.6,3.98,1.25,7.46,3.15,10.48,5.96,3.31,3.09,7.01,5.36,12.16,3.24,0,1.89,.07,3.49-.02,5.08-.14,2.58-1.92,4.09-4.57,4.01-4.04-.12-8.07-.31-12.11-.31-2.64,0-3.06,.45-3.03,3,.21,14.4,.48,28.79,.71,43.19,.05,2.97,0,5.94,0,9.09h-74.25c-.31-38.9-.61-77.79-.92-116.75Z"
          />
        </Link>
        <Link
          to="/Counties/539"
          onFocus={(e) => handleFocus(e, "Fremont")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Fremont County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Fremont")}
            className={
              activeCounty === "Fremont"
                ? "active-county-section"
                : "county-section"
            }
            d="M568.48,546.5c3.2,3.49,6.12,6.78,9.16,9.96,.99,1.04,1.6,2.01,1.14,3.42-.14,.42-.12,.89-.18,1.34-.54,3.6,.31,6.33,4.18,8.02,3.47,1.52,6.74,3.51,9.88,5.6,.94,.63,1.52,2.27,1.6,3.49,.54,8.5,1.09,17.01,1.29,25.52,.28,12.33,.23,24.66,.4,36.99,.02,1.44-.58,1.95-1.88,2.42-7.18,2.61-14.4,5.07-22.11,5.82-.73,.07-1.46,.26-2.16,.48-3.75,1.17-7.43,2.19-11.54,2.02-2.76-.11-5.79,.79-8.34,1.96-7.15,3.29-14.55,2.79-21.98,1.86-3.04-.38-5.96-1.54-8.97-2.16-2.48-.51-5.01-.83-7.54-1.07-1.12-.11-1.73-.3-2.18-1.46-1.07-2.74-3.5-3.76-6.39-3.94-2.42-.15-4.84-.2-7.26-.39-3.04-.24-3.83-1-4.31-3.87-.63-3.79-.42-4.08,3.46-4.86,1.44-.29,2.93-.47,4.31-.95,2.99-1.04,4.34-3.09,4.28-6.09-.08-3.49,.11-3.67,3.53-4.85,1.83-.63,3.65-1.35,5.34-2.25,5.05-2.69,7.24-7.07,7.69-12.42,.06-.71,.16-1.42,.17-2.12,.05-2.97,.25-5.72,3.32-7.65,1.8-1.13,2.03-3.37,2.02-5.45-.02-2.97,.58-3.57,3.72-3.84,2.14-.19,4.29-.31,6.44-.44,4.41-.28,6.46-2.38,6.26-6.61-.19-4.05-.49-8.09-.74-12.11,1.51-.36,2.92-.77,4.36-1.01,.5-.09,1.11,.14,1.6,.37,1.14,.53,2.19,1.27,3.37,1.7,1.74,.63,4.12-.23,4.82-1.83,.25-.56-.07-1.61-.52-2.14-1.3-1.56-3.55-2.41-3.62-4.86-.1-3.66,.3-7.22,1.98-10.55,.19-.37,.58-.71,.97-.91,4.16-2.08,8.34-4.14,12.52-6.19,.71-.35,1.42-.68,1.93-.92Z"
          />
        </Link>
        <Link
          to="/Counties/525"
          onFocus={(e) => handleFocus(e, "Boise")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Boise County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Boise")}
            className={
              activeCounty === "Boise"
                ? "active-county-section"
                : "county-section"
            }
            d="M183.44,601.31c.88,.66,1.76,1.32,2.63,1.98,2.25,1.69,4.33,3.7,6.81,5,3.08,1.62,5.16,3.36,5.14,7.08-.02,3.54,2.32,5.92,5.44,7.61,1.23,.66,2.52,1.25,3.68,2,4.65,3.02,5.95,8.16,3.35,12.95-1.52,2.8-1.36,3.72,1.32,5.77,1.9,1.45,3.15,3.22,3.24,5.55,.02,.54-.54,1.43-1.05,1.62-4.7,1.74-8.18,.54-10.47-3.76-1.25-2.34-2.08-4.89-3.1-7.34-.4-.96-.66-1.99-1.21-2.86-.85-1.34-1.58-2.94-3.79-2.58-2.24,.36-4.32,1.02-4.91,3.38-.63,2.5-1.09,5.05-1.43,7.6-.65,4.84-3.19,8.18-8.13,9.57-3.09,.87-6.26,1.51-9.39,2.25-5.56,1.32-9.36,4.37-10.67,9.91-.47,1.98-1.63,3.54-3.47,4.57-2.86,1.6-4.55,4-5.23,7.04-.64,2.89-2.25,5.11-4.76,6.71-4.82,3.07-9.66,6.13-14.56,9.08-4.14,2.49-8.99,2.49-13.62,3.14-.51,.07-1.25-.41-1.68-.83-8.45-8.25-16.85-16.55-25.3-24.8-1.36-1.33-1.75-2.62-1.23-4.51,.56-2.03,.69-4.21,.69-6.32,.03-9.82-.03-19.64-.1-29.46-.02-3.51,2.85-7.12,6.39-7.76,.67-.12,1.44,.21,2.15,.38,4.48,1.08,7.19-.78,7.51-5.16,.15-2.12,1.06-3.22,3.36-3.43,2.94-.26,5.91-.33,8.87-.31,14.22,.09,28.43,.29,42.65,.34,6.01,.02,11.09-2.31,15.43-6.23,1.35-1.22,2.07-2.47,1.79-4.41-.42-2.91,.27-3.44,3.65-3.78Z"
          />
        </Link>
        <Link
          to="/Counties/526"
          onFocus={(e) => handleFocus(e, "Bonner")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Bonner County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Bonner")}
            className={
              activeCounty === "Bonner"
                ? "active-county-section"
                : "county-section"
            }
            d="M35.59,20.52c5.04,.47,10.06,.92,15.08,1.43,4.08,.41,4.9,1.34,4.9,5.34,0,10.98,0,21.97,0,32.95,0,3.16,2.04,5.02,5.36,5.11,17.97,.49,35.94,1.02,53.91,1.52,2.14,.06,4.29,0,6.6,0-.23,20.68-.45,41.16-.68,62.08-1.5-.6-2.48-.95-3.41-1.38-3.82-1.76-7.63-3.55-11.44-5.32-2.84-1.32-4.76-.97-6.9,1.18-1.73,1.74-2.8,3.74-3.1,6.16-.52,4.22-1.1,8.44-1.72,13.18-3.2,0-6.6,.14-9.99-.05-2.03-.12-3.18-1.61-3.52-3.92-.23-1.53-.29-3.09-.53-4.62-.51-3.24-1.83-4.36-5.26-4.4-6.06-.06-12.13-.07-18.19-.07-7.62-.01-15.23,0-22.85,0h-2.29c1.34-36.46,2.67-72.75,4.01-109.19Z"
          />
        </Link>
        <Link
          to="/Counties/527"
          onFocus={(e) => handleFocus(e, "Bonneville")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Bonneville County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Bonneville")}
            className={
              activeCounty === "Bonneville"
                ? "active-county-section"
                : "county-section"
            }
            d="M600.59,766.92c-17.12-.24-33.93,.98-50.97,.06,0-1.01,0-1.77,0-2.52-.04-7.62-.01-15.25-.17-22.87-.08-4.07-2.29-6.18-6.49-6.48-4.3-.31-8.6-.55-12.9-.69-2.19-.07-3.36-1.05-3.84-3-.02-.06-.05-.12-.07-.18-1.92-6.67-3.38-7.59-10.61-6.8-5.4,.58-10.33-.69-14.6-3.89-2.7-2.02-5.67-2.83-9.01-2.8-7.48,.06-14.96,.06-22.44,.16-1.74,.02-3.48,.24-5.21,.5-4.02,.6-6.75-1.55-6.76-5.44-.01-6.25,0-12.5,0-19.06,7.43-.48,14.65-1.22,21.89-1.36,16.44-.32,32.88-.28,49.32-.53,15.42-.24,30.83-.7,46.25-1,5.14-.1,6.33,.66,8.06,5.3,1.24,3.3,3.36,5.15,7.12,5.17,1.81,0,3.62,.26,5.44,.37,2.97,.17,3.03,.17,3.31,2.93,.39,3.86,.85,7.71,1,11.58,.55,14.32,1.03,28.65,1.42,42.98,.07,2.42-.47,4.85-.75,7.6Z"
          />
        </Link>
        <Link
          to="/Counties/532"
          onFocus={(e) => handleFocus(e, "Caribou")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Caribou County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Caribou")}
            className={
              activeCounty === "Caribou"
                ? "active-county-section"
                : "county-section"
            }
            d="M600.66,768.48c1.57,4.92,3.93,52.25,3.3,65.71-1.93,.48-3.95,.91-5.92,1.47-2.77,.79-5.52,1.65-8.26,2.53-2.02,.65-2.87,.27-2.87-1.77,0-2.12,.45-4.23,.56-6.35,.06-1.04,.01-2.16-.34-3.13-.66-1.82-1.55-3.57-2.4-5.33-1.1-2.28-3.19-2.63-4.81-.62-1.05,1.3-1.73,2.89-2.46,4.41-1.29,2.7-1.58,2.84-4.35,1.55-1.39-.65-2.76-1.33-4.15-1.99-2.87-1.37-5.79-1.78-9.01-1.12-2.28,.47-4.69,.32-7.04,.49-1.27,.1-2.58,.15-3.78,.5-2.04,.58-2.89,2.3-2.34,4.72,.9,3.9,1.99,7.77,2.87,11.68,.39,1.73,.45,3.52,.68,5.48-1.99,.11-3.66,.25-5.32,.26-.92,0-1.92,0-2.09-1.31-.04-.32-.59-.8-.93-.83-2.27-.19-4.82-1.55-6.47,1.27-.11,.19-.57,.26-.87,.26-3.09,.02-6.18,.01-9.44,.01-.27-1.88-.48-3.79-.85-5.66-.29-1.45-.54-2.98-1.22-4.28-3.35-6.33-4.5-13.34-6.86-19.98-.17-.49-.31-.99-.49-1.47-1.88-5.08-5.94-7.22-11.45-6.05-1.42,.3-2.85,.56-4.54,.89-.75-4.66-1.56-9.03-2.14-13.43-.39-3-.73-6.04-.59-9.05,.23-4.82,3.25-8.64,5.51-12.74,1.4-2.54,3.34-3.33,6.2-3.41,8.27-.25,16.55-.68,24.81-1.18,4.64-.28,9.25-.89,13.88-1.21,1.99-.14,4,.1,6,.11,4.51,.02,9.02,.04,13.53-.01,10.84-.12,21.69-.27,32.53-.41,.33,0,.66,0,1.1,0Z"
          />
        </Link>
        <Link
          to="/Counties/534"
          onFocus={(e) => handleFocus(e, "Clark")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Clark County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Clark")}
            className={
              activeCounty === "Clark"
                ? "active-county-section"
                : "county-section"
            }
            d="M409.6,616.6c3.55-.18,7.03-.34,10.51-.55,1.07-.06,2.16-.15,3.19-.41,1.75-.45,3.21-1.59,3.35-3.27,.34-4.07,1.37-8.18-.38-12.25-.35-.81,.23-1.96,.23-2.96,0-1.22,.19-2.6-.32-3.63-1.49-3.01-.5-5.57,1.24-7.99,1.76-2.46,4.1-4.19,7.46-3.73,7.79,1.08,15.57,2.25,23.35,3.38,1.13,.16,2.26,.36,3.4,.49,2.86,.32,5.2-.61,6.81-2.91,.93-1.33,1.72-2.77,2.45-4.22,2.13-4.18,5.16-5.83,9.93-5.19,2.2,.3,4.36,.82,6.54,1.18,5.27,.87,10.42,.38,15.59-.93,3.43-.87,6.99-1.31,10.51-1.79,1.93-.26,3.67,.51,4.76,2.04,1.76,2.47,4.13,2.39,6.63,1.67,1.8-.52,3.55-1.22,5.27-1.95,2.89-1.23,5.88-1.11,9.19-.87,.26,4.61,.61,9.16,.74,13.72,.05,1.84-1.37,3.03-3.58,3.29-2.81,.32-5.63,.46-8.44,.75-3.07,.32-4.55,1.8-4.4,4.72,.16,3.04-1.06,5.18-3.64,6.86-.68,.45-1.18,1.49-1.3,2.32-.37,2.55-.51,5.13-.77,7.7-.51,5.19-3.47,8.68-8.29,10.83-1.34,.6-2.75,1.04-4.13,1.54-3.32,1.18-4.13,2.31-4.01,5.62,.12,3.14-1.01,4.67-4.18,5.42-2.02,.48-4.08,.83-6.12,1.23-.32,.06-.71,.16-.97,.04-4.69-2.06-9.64-1.38-14.53-1.29-8.75,.16-17.51,.28-26.26,.46-2.02,.04-4.04,.21-6.04,.44-4.06,.46-5.96,2.51-6.03,6.42-.03,1.74,0,3.48,0,5.35-.52,.05-.96,.13-1.4,.12-6.67-.1-13.33-.13-19.99-.35-3.9-.13-5.62-2.02-5.71-5.84-.16-7.62-.31-15.24-.48-22.85-.02-.83-.13-1.66-.21-2.61Z"
          />
        </Link>
        <Link
          to="/Counties/561"
          onFocus={(e) => handleFocus(e, "Washington")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Washington County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Washington")}
            className={
              activeCounty === "Washington"
                ? "active-county-section"
                : "county-section"
            }
            d="M87.08,588.66c-1,6.9-2,13.66-2.96,20.42-.48,3.39-.93,6.78-1.38,10.18-.34,2.55-1.74,3.95-4.27,5.07-2.14,.95-3.91,.48-5.92,.31-6.43-.54-12.86-1.11-19.3-1.35-6.93-.26-13.87-.14-20.81-.25-.54,0-1.22-.44-1.58-.87-1.81-2.17-3.55-4.39-5.28-6.62-2.68-3.44-6.05-5.38-10.69-5.77-3.01-.25-5.96-1.49-8.86-2.49-1.83-.63-2.74-2.17-3.09-4.02-1.94-10.09-1.56-19.84,3.65-29.18,2.79-4.99,4.94-10.31,7.35-15.5,2.57-5.52,6.31-10.17,11.06-14.14,3.65-3.05,7.15-6.28,10.77-9.37,.47-.4,1.27-.7,1.88-.65,5.03,.41,10.05,.93,15.08,1.37,4.37,.38,5.52,1.55,5.31,5.83-.31,6.31,.45,12.5,1.92,18.63,.5,2.1,1.6,3.61,3.62,4.59,3.08,1.5,3.7,2.82,3.48,6.05-.15,2.25-.15,4.52-.04,6.77,.14,2.72,1.4,4.96,4.13,5.94,4.59,1.63,9.27,3.02,13.92,4.5,.69,.22,1.41,.38,2,.53Z"
          />
        </Link>
        <Link
          to="/Counties/556"
          onFocus={(e) => handleFocus(e, "Power")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Power County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Power")}
            className={
              activeCounty === "Power"
                ? "active-county-section"
                : "county-section"
            }
            d="M413.14,861.71c-.35-5.89-.74-11.55-1.01-17.21-.22-4.62,.42-9.26-1.01-13.83-.72-2.31-1.68-3.41-4.22-3.22-6.13,.47-11.31-1.7-16.18-4.99-1.13-.77-1.26-1.45-.68-2.57,.86-1.66,2.26-2.46,4.07-2.85,2.8-.6,3.8-2,3.54-4.71-.15-1.61-.26-3.22-.31-4.83-.03-1.25-.4-1.96-1.86-2.27-6.04-1.29-6.61-1.96-6.82-7.92-.36-9.94-.67-19.88-.95-29.82-.13-4.54,1.57-6.2,6.26-6.44,4.5-.23,8.99-.53,13.93-.82,.26,4.29,.66,8.44,.74,12.61,.12,6.59,.09,13.18,0,19.77-.03,1.84,.58,2.34,2.46,2.21,5.24-.35,10.49-.48,15.73-.81,.73-.05,1.59-.59,2.08-1.16,2.56-2.97,5.02-6.02,7.48-9.01,.3,.11,.46,.12,.54,.2,2.81,2.74,3.91,2.79,6.54-.21,2.02-2.31,4.75-3.32,7.51-4.28,.56-.19,1.59,.25,2.12,.71,3.94,3.36,5,7.67,4.78,12.6-.24,5.15,1.37,9.88,4.3,14.28,2.68,4.02,4.99,8.27,7.42,12.43,1.82,3.11,2,6.37,.73,9.72-.69,1.82-1.24,3.69-1.99,5.48-.3,.72-.9,1.36-1.49,1.92-.57,.54-1.25,1-1.96,1.36-2.74,1.41-4.28,3.22-3.97,6.56,.26,2.9-1.18,4.01-4.29,4.23-1.87,.13-3.77-.04-5.65-.01-3.55,.06-5.62,1.96-5.9,5.34-.04,.45-.05,.9-.07,1.35-.11,2.95-1.23,4.22-4.34,4.56-2.67,.29-5.37,.39-8.06,.54-6.38,.36-12.77,.71-19.47,1.08Z"
          />
        </Link>
        <Link
          to="/Counties/519"
          onFocus={(e) => handleFocus(e, "Adams")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Adams County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Adams")}
            className={
              activeCounty === "Adams"
                ? "active-county-section"
                : "county-section"
            }
            d="M101.34,575.42c-2.36-.03-3.37,1.55-4,3.29-.74,2.04-2.11,3.15-4.24,3.68-2.5,.62-4.14,2.3-5.27,4.66-5.43-1.39-10.94-2.29-15.92-4.87-2.04-1.06-3.12-2.61-3.14-4.81-.01-2.32-.05-4.65,.1-6.96,.21-3.05-.99-5.38-3.82-6.67-2.19-1-3.17-2.53-3.63-4.72-1.22-5.78-2.04-11.57-1.64-17.49,.34-4.92-1.65-6.9-6.87-7.32-5.15-.41-10.29-.93-15.44-1.4,.87-9.96,2.64-19.39,7.33-28.18,4.17-7.8,8.01-15.77,12.07-23.63,.27-.52,1.19-1.07,1.78-1.04,7.6,.37,15.2,.74,22.78,1.34,5.6,.44,8.24,3.63,8.92,10.16,.16,1.54,.29,3.1,.66,4.6,1.01,4.1,4.12,6.51,8.5,6.72,.27,.01,.54,0,.81,.02,6.1,.28,9.32,4.45,7.53,10.09-1.19,3.76-2.63,7.46-4.21,11.09-3.73,8.6-4.13,17.42-3,26.59,.82,6.64,.5,13.41,.67,20.13,.04,1.54,0,3.08,0,4.74Z"
          />
        </Link>
        <Link
          to="/Counties/545"
          onFocus={(e) => handleFocus(e, "Kootenai")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Kootenai County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Kootenai")}
            className={
              activeCounty === "Kootenai"
                ? "active-county-section"
                : "county-section"
            }
            d="M94.81,144.51c0,2.44,.02,5.06,0,7.68-.11,14.79-.19,29.59-.37,44.38-.03,2.11-.47,4.23-.92,6.31-.11,.51-1.02,1.26-1.52,1.23-2.74-.15-5.48-.47-8.21-.8-5.55-.67-10.91-.17-15.88,2.46-3.71,1.96-7.68,2.67-11.85,2.67-8.22,0-16.44,0-24.66,0-.25,0-.51-.09-.74-.13-.94-7.34-.76-14.6-.08-21.87,.33-3.53,.48-7.09,.51-10.64,.12-14.08,.15-28.17,.22-42.25,0-.64,.06-1.27,.1-2.1,.72-.04,1.37-.1,2.01-.1,13.01,0,26.01-.01,39.02,0,5.54,0,5.74,.2,6.27,5.44,.09,.9,.19,1.8,.35,2.69,.59,3.24,2.7,5,6.11,5.04,3.01,.04,6.03,0,9.65,0Z"
          />
        </Link>
        <Link
          to="/Counties/528"
          onFocus={(e) => handleFocus(e, "Boundary")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Boundary County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Boundary")}
            className={
              activeCounty === "Boundary"
                ? "active-county-section"
                : "county-section"
            }
            d="M78.85,2.46c11.85,.3,23.7,.58,35.55,.9,1.95,.05,3.9,.21,5.84,.4,1.57,.15,2.47,.73,2.44,2.57-.31,18.99-.55,37.98-.76,56.97-.02,1.55-.49,2.12-2.19,2.06-15.48-.52-30.96-.98-46.44-1.44-4.04-.12-8.08-.2-12.12-.27-2.61-.05-3.91-1.27-3.91-3.77,0-10.86,0-21.72,0-32.57,0-4.98-1.15-6.35-6.25-6.96-4.01-.48-8.04-.78-12.06-1.08-2.95-.22-3.43-.64-3.36-3.36,.09-3.36,.16-6.72,.24-10.07,.07-2.84,.74-3.89,3.67-4.11,3.4-.25,6.85-.06,10.28,.03,9.69,.25,19.39,.54,29.08,.82,0-.04,0-.07,0-.11Z"
          />
        </Link>
        <Link
          to="/Counties/549"
          onFocus={(e) => handleFocus(e, "Lincoln")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Lincoln County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Lincoln")}
            className={
              activeCounty === "Lincoln"
                ? "active-county-section"
                : "county-section"
            }
            d="M255.31,793.35c0-.89,0-1.66,0-2.42-.1-11.11-.2-22.21-.33-33.32-.01-1.15-.17-2.33-.44-3.45-.4-1.71,.49-2.6,2-3.03,.95-.27,1.98-.4,2.98-.41,7.13-.05,14.25-.08,21.38-.08,18.73,0,37.46,.04,56.19,.06,.79,0,1.58,0,2.58,0,.24,5.15,.46,10.1,.71,15.06,.43,8.7,.85,17.4,1.34,26.1,.07,1.33-.21,2.11-1.71,2.23-1.87,.15-2.61,1.11-2.61,2.95,0,2.06-.37,4.11-.5,6.17-.06,1-.45,1.47-1.54,1.59-5.15,.57-10.28,1.47-15.44,1.7-3.03,.13-6.11-.76-9.15-1.28-.37-.06-.77-.66-.93-1.08-.44-1.11-.42-2.88-1.2-3.28-1.58-.81-3.58-1.23-5.4-1.21-5.45,.07-10.89,.46-16.34,.68-1.2,.05-2.43,0-3.62-.15-1.7-.21-3.02-.67-3.69-2.62-.92-2.67-3.54-3.36-6.22-3.47-5.51-.23-11.03-.37-16.55-.56-.4-.01-.8-.09-1.5-.18Z"
          />
        </Link>
        <Link
          to="/Counties/553"
          onFocus={(e) => handleFocus(e, "Oneida")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Oneida County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Oneida")}
            className={
              activeCounty === "Oneida"
                ? "active-county-section"
                : "county-section"
            }
            d="M413.34,863.38c6.1-.35,12.2-.71,18.31-1.05,2.89-.16,5.79-.26,8.67-.47,4.27-.3,6.24-2.34,6.36-6.46,.11-3.71,1.42-4.92,5.35-4.9,1.48,0,2.97,.18,4.44,.07,4.42-.34,6.2-2.23,6.34-6.47,.03-.93-.1-2.2,.45-2.71,1.38-1.29,3.09-2.27,4.74-3.27,.22-.14,.85,.21,1.22,.44,2.43,1.51,4.89,2.98,7.25,4.59,3.8,2.6,5.04,6.16,3.97,10.54-.47,1.94-.71,3.93-1.07,5.9-.24,1.33,.3,1.84,1.67,1.47,1.94-.52,3.94-.91,5.8-1.62,2.55-.97,4.68-.63,6.67,1.17,1.77,1.59,3.64,3.07,5.49,4.58,4.12,3.39,6.26,9.99,3.78,14.54-3.19,5.86-2.82,11.81-2.11,17.94,.12,1.03,.2,2.06,.29,3.09,.02,.25-.02,.5-.05,1.19-28.73,.76-57.43,1.52-86.37,2.29-1.43-13.54-2.48-27-1.22-40.86Z"
          />
        </Link>
        <Link
          to="/Counties/543"
          onFocus={(e) => handleFocus(e, "Jefferson")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Jefferson County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Jefferson")}
            className={
              activeCounty === "Jefferson"
                ? "active-county-section"
                : "county-section"
            }
            d="M441.02,692.43c-2.18-3.41-1.97-7.03-1.96-10.64,.04-12.47,0-24.94,.03-37.41,0-.96,.11-1.93,.24-2.89,.27-2,1.53-3.15,3.59-3.33,3.28-.29,6.57-.61,9.87-.69,9.76-.23,19.53-.4,29.29-.48,1.99-.02,3.98,.46,5.97,.77,1.06,.17,1.7,.73,1.38,1.91-.1,.37-.06,.77-.04,1.16,.32,5.76,1.9,7.21,7.93,7.38,1.88,.05,3.78,.09,5.65,.28,3.7,.39,5.12,2.1,5.01,5.71-.13,4.58-.32,9.17-.21,13.75,.05,2.15,.53,4.36,1.23,6.41,.71,2.1,1.98,2.48,4.1,1.63,7-2.8,5.74-2.58,11.36,2.06,3.43,2.84,6.91,5.66,11.58,6.34,1.69,.25,3.46,.04,5.58,.04,0,1.52,.09,3.1-.07,4.65-.03,.34-.95,.82-1.48,.84-3.83,.19-7.67,.34-11.5,.39-8.69,.12-17.38,.18-26.07,.27-9.43,.1-18.87,.06-28.29,.38-6.32,.21-12.61,1.1-18.92,1.41-4.83,.24-9.69,.05-14.25,.05Z"
          />
        </Link>
        <Link
          to="/Counties/520"
          onFocus={(e) => handleFocus(e, "Bannock")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Bannock County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Bannock")}
            className={
              activeCounty === "Bannock"
                ? "active-county-section"
                : "county-section"
            }
            d="M520.77,868.07c-1.39,0-2.46-.02-3.53,0-1.62,.04-2.83-.28-2.55-2.26,.17-1.2-.68-1.65-1.75-1.59-3.08,.16-6.18,.23-9.21,.69-1.42,.21-2.36,.11-3.39-.84-1.71-1.56-3.63-2.9-5.31-4.47-2.69-2.52-5.65-2.98-9.08-1.63-1.42,.56-2.93,.87-4.86,1.43,.34-1.86,.53-3.42,.93-4.93,1.47-5.57-.06-9.72-4.94-13-2.04-1.37-4.12-2.68-6.25-3.93-.98-.57-1.27-1.14-.85-2.2,.73-1.8,1.32-3.65,1.99-5.48,1.38-3.79,1.25-7.49-.8-11.03-2.49-4.28-4.89-8.63-7.6-12.77-2.67-4.07-4.28-8.42-3.94-13.17,.41-5.61-1.45-10.23-5.4-14.24-1.18-1.2-2.56-1.14-3.91-.69-3.19,1.06-6.22,2.38-8.46,5-.56,.66-1.23,2.11-2.53,1.09-1.1-.87-1.85-1.85-1-3.59,3.99-8.07,4.33-8.39,13.71-8.5,15.83-.17,31.66-.31,47.49-.46,.92,0,1.83,0,3.01,0-1.27,2.07-2.31,3.85-3.45,5.57-3.57,5.38-4.6,11.2-3.51,17.45,.88,5.03,1.71,10.06,2.48,15.1,.26,1.67,1.03,2.22,2.72,1.76,1.23-.33,2.5-.5,3.74-.78,4.89-1.09,8.14,.58,9.66,5.22,1.59,4.86,2.97,9.78,4.49,14.66,.32,1.04,.75,2.07,1.27,3.03,2.07,3.79,3.12,7.81,3.28,12.07,.01,.31,.07,.75-.11,.93-2.54,2.5-2.15,5.75-2.31,8.76-.22,4.17-.06,8.37-.06,12.79Z"
          />
        </Link>
        <Link
          to="/Counties/530"
          onFocus={(e) => handleFocus(e, "Camas")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Camas County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Camas")}
            className={
              activeCounty === "Camas"
                ? "active-county-section"
                : "county-section"
            }
            d="M273.47,735.2v13.22c-4.67,.23-9.24,.36-13.8,.7-2.08,.16-4.12,.74-6.79,1.24-1.39-1.11-3.8-1.31-6.35-1.3-11.93,.05-23.85,.03-35.78,.01-1.53,0-3.06-.17-4.66-.27,0-4.01-.07-7.81,.01-11.61,.21-10.13,.42-20.26,.75-30.39,.18-5.35,1.35-10.46,4.29-15.15,2.14-3.4,2.86-7.12,1.44-10.97-2.05-5.58,.68-10.04,3.59-14.45,.21-.31,1.45-.31,2-.04,2.94,1.42,5.75,3.1,8.72,4.45,3.94,1.8,8.13,2.64,12.53,2.45,2.75-.12,3.97,.76,4.32,3.47,.52,4.03,.85,8.09,1.07,12.15,.6,10.88,.07,9.25,7.27,16.71,1.74,1.8,3.51,3.57,5.3,5.32,1.98,1.94,2.62,4.15,2.31,6.89-.2,1.72,.45,3.52,.73,5.28,.34,2.21,.91,4.42,.98,6.64,.1,3.13,.97,4.36,4.24,4.8,2.53,.34,5.07,.55,7.8,.84Z"
          />
        </Link>
        <Link
          to="/Counties/521"
          onFocus={(e) => handleFocus(e, "Bear Lake")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Bear Lake County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Bear Lake")}
            className={
              activeCounty === "Bear Lake"
                ? "active-county-section"
                : "county-section"
            }
            d="M604.54,835.56c.39,7.12,.86,13.67,1.08,20.22,.42,12.33,.66,24.66,1.04,36.98,.14,4.66-.89,5.98-5.76,6.18-7.94,.32-15.89,.48-23.83,.65-3.69,.08-7.39,.01-11.47,.01,0-1.1,.05-2.43,0-3.76-.2-4.92-2.75-7.31-7.4-6.8-2.07,.22-4.91-2.3-5.1-4.68-.65-8.19-1.01-16.38,1.42-24.41,1.11-3.67,.79-7.11-1.44-10.39-.6-.88-1.09-2.03-1.08-3.05,.03-5.46-1.97-10.51-3.16-15.73-.9-3.91-.31-4.67,3.91-4.89,3.15-.17,6.29-.67,9.43-.71,1.76-.02,3.59,.41,5.26,.99,2.14,.75,4.11,1.91,6.2,2.79,2.35,.99,3.6,.56,4.79-1.64,.8-1.49,1.41-3.07,2.25-4.54,.32-.55,1.06-.88,1.61-1.31,.39,.48,.8,.95,1.15,1.45,.22,.32,.3,.72,.51,1.05,1.79,2.84,2.29,5.82,1.36,9.06-.28,.97-.24,2.05-.21,3.08,.09,3.53,1.97,4.67,5.43,3.57,4.39-1.4,8.84-2.63,14.01-4.15Z"
          />
        </Link>
        <Link
          to="/Counties/546"
          onFocus={(e) => handleFocus(e, "Latah")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Latah County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Latah")}
            className={
              activeCounty === "Latah"
                ? "active-county-section"
                : "county-section"
            }
            d="M92.27,306.97c-3.09,0-6.12,.2-9.11-.07-1.89-.17-3.77-.89-5.53-1.63-4.28-1.8-8.64-2.16-12.94-.47-1.85,.73-3.42,2.21-4.99,3.48-1.79,1.45-3.32,3.24-5.22,4.53-1.24,.84-2.95,1.48-4.45,1.48-7-.03-14-.32-21-.53-.45-.01-.9-.16-1.79-.32,.68-23.98,1.35-47.94,2.03-71.91,2.88,.25,4.39,1.62,4.77,4.33,.17,1.21,.18,2.45,.08,3.67-.11,1.33,.46,1.85,1.78,1.99,3.27,.36,6.53,.81,9.81,1.2,2.26,.26,4.18,1.01,5.98,2.48,1.17,.95,2.97,1.59,4.52,1.65,11.03,.42,22.07,.69,33.11,.92,1.41,.03,1.73,.56,2.32,1.69,2.5,4.8,.92,9.65,.76,14.54-.27,8.12-.1,16.26-.12,24.39,0,2.77,0,5.55,0,8.59Z"
          />
        </Link>
        <Link
          to="/Counties/518"
          onFocus={(e) => handleFocus(e, "Ada")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Ada County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Ada")}
            className={
              activeCounty === "Ada"
                ? "active-county-section"
                : "county-section"
            }
            d="M67.56,735.07c.64-3.6,1.27-7.15,1.89-10.7,.14-.83,.28-1.66,.35-2.49,.17-2.34,.55-4.38,3.21-5.6,1.99-.92,2.78-3.05,2.55-5.15-.65-5.83-1.28-11.68-2.33-17.46-.46-2.53-2.36-4.82-2.82-7.35-.81-4.44-.99-8.98-1.51-13.46-.14-1.18,.38-1.71,1.45-1.86,2.51-.35,5.03-.83,7.56-.93,3.22-.12,6.45,.06,9.68,.18,.54,.02,1.22,.21,1.57,.56,5.34,5.37,10.61,10.8,15.95,16.16,3.58,3.6,7.24,7.12,10.92,10.63,1.08,1.03,1.53,2.09,1.44,3.59-.1,1.59,.31,3.2,.34,4.8,.04,2.7,.05,5.4-.11,8.09-.18,3.01-.72,6.01-.81,9.02-.28,10.33-.38,20.66-.66,30.98-.1,3.95-1.03,4.74-5.14,4.75-5.04,.01-10.11-.31-14.9,1.77-.53,.23-1.35,.09-1.96-.1-7.66-2.34-13.41-6.52-15.05-14.59-.17-.82-.49-1.63-.54-2.46-.22-3.61-2.55-5.49-5.8-6.63-1.69-.59-3.41-1.14-5.28-1.76Z"
          />
        </Link>
        <Link
          to="/Counties/522"
          onFocus={(e) => handleFocus(e, "Benewah")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Benewah County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Benewah")}
            className={
              activeCounty === "Benewah"
                ? "active-county-section"
                : "county-section"
            }
            d="M93.19,206.04c.82,2.61,1.31,5.35,.27,8.19-.26,.7-.27,1.52-.26,2.28,.12,10.33,.36,20.65,.32,30.98,0,2.45-.87,4.91-1.44,7.35-.22,.93-.86,1.43-2.05,1.39-11.3-.36-22.6-.62-33.9-1.05-1.26-.05-2.66-.78-3.67-1.57-1.86-1.45-3.89-2.21-6.23-2.47-3.07-.34-6.14-.7-9.19-1.19-.45-.07-.98-.81-1.11-1.32-.21-.86-.08-1.79-.17-2.69-.28-2.99-1.85-5.01-4.86-6.04-.58-.2-1.37-.95-1.36-1.44,.15-9.29,.4-18.58,.65-27.86,0-.11,.11-.22,.26-.5,3.87,0,7.82,.01,11.78,0,5.72-.02,11.46,.16,17.16-.19,2.74-.17,5.63-.95,8.07-2.16,5.97-2.97,12.1-3.62,18.6-2.42,2.29,.42,4.65,.5,7.12,.74Z"
          />
        </Link>
        <Link
          to="/Counties/552"
          onFocus={(e) => handleFocus(e, "Nez Perce")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Nez Perce County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Nez Perce")}
            className={
              activeCounty === "Nez Perce"
                ? "active-county-section"
                : "county-section"
            }
            d="M27.3,315.92c6.79,0,13.35-.37,19.83,.11,5.85,.44,10.03-1.96,13.21-5.99,4.18-5.29,12.03-6,18.58-2.23,.4,.23,.55,1.08,.56,1.65,.09,3.81,.12,7.62,.16,11.42,.02,2.26,.51,2.7,2.88,2.57,.67-.04,1.35-.19,2.01-.14,.68,.05,1.35,.27,2.03,.42-.22,.72-.34,1.49-.7,2.15-.22,.41-.8,.65-1.23,.95-2.17,1.54-2.56,3.34-1.1,5.54,.61,.92,1.37,1.76,2.09,2.61,2.28,2.68,2.29,4.02-.09,6.9-4.29-2.22-8.34-.94-12.45,.81-1.08,.46-2.49,.33-3.74,.29-5.72-.21-11.43-.51-17.14-.75-2.11-.09-2.56,.42-1.43,1.99,2.89,4.02,3.23,8.22,3.05,13.09-.3,8.47,.79,16.99,1.19,25.5,.04,.95-.36,2.14-.99,2.87-2.99,3.41-3.88,7.43-4.11,11.7-.11,2.08-.21,4.17-2.87,5.68-1.46-1.89-3.28-3.66-4.35-5.77-2.71-5.33-5.05-10.82-7.6-16.23-1.1-2.33-.96-4.55,0-6.92,.74-1.82,1.13-3.8,1.48-5.75,.13-.7-.07-1.67-.51-2.21-1.66-2.02-1.41-4.44-1.95-6.72-.81-3.42-1.95-6.83-3.51-9.99-2.05-4.14-4.62-8.08-5.08-12.74-.26-2.61-.12-5.28,.14-7.9,.44-4.42,1.11-8.82,1.64-12.91Z"
          />
        </Link>
        <Link
          to="/Counties/541"
          onFocus={(e) => handleFocus(e, "Gooding")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Gooding County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Gooding")}
            className={
              activeCounty === "Gooding"
                ? "active-county-section"
                : "county-section"
            }
            d="M250.06,819.9c-4.36-.52-8.47-.79-12.48-1.54-3.68-.69-6.54-2.53-7.44-6.46-.82-3.6-3.07-6.06-6.9-7.13-2.08-.58-2.71-1.98-1.85-3.94,1.97-4.49,1.06-8.85-.45-13.22-.39-1.12-1.13-1.38-2.33-1.26-4.07,.4-8.15,.69-12.41,1.04v-36.69c4.61,0,9.17,0,13.73,0,9.03,0,18.06,0,27.09,0,.87,0,1.75,.04,2.61,.19,1.92,.32,3.16,1.28,3.24,3.32,.2,4.77,.59,9.53,.66,14.3,.12,8.27,.06,16.54,.06,24.81,0,.38,.02,.82-.16,1.12-2.35,3.8-2.03,8.1-2.42,12.24-.41,4.36-.64,8.74-.95,13.21Z"
          />
        </Link>
        <Link
          to="/Counties/551"
          onFocus={(e) => handleFocus(e, "Minidoka")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Minidoka County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Minidoka")}
            className={
              activeCounty === "Minidoka"
                ? "active-county-section"
                : "county-section"
            }
            d="M341.51,750.65c5.47,0,10.77,0,16.08,0,2.43,0,4.85-.02,7.28,.07,3.17,.11,4.1,.99,4.16,4.01,.19,10.46,.34,20.92,.47,31.39,.05,3.7-1.15,7.02-3.77,9.75-1.29,1.34-1.76,2.84-1.75,4.58,.01,4.12,0,8.24,0,12.67-2.33,.34-4.7,.65-7.06,1.03-3.93,.63-4.95,1.79-4.72,5.61,.23,3.96,.1,7.81-1.29,11.64-.63,1.73-1.47,3-3.05,3.86-1.22,.67-2.52,1.23-3.77,1.84-1.41,.69-2.27,.36-3.65-.65-2.97-2.16-6.62-2.87-10.44-2.67-2.66,.14-5.36,0-8.02-.19-.5-.04-1.31-.97-1.34-1.52-.36-7.74-.61-15.48-.88-23.23,0-.12,.07-.24,.17-.53,5.06-.61,10.19-1.25,15.33-1.83,3.06-.34,3.17-.41,3.4-3.4,.18-2.32,.39-4.63,.58-6.89,.14-.08,.19-.12,.26-.15,4.17-1.51,4.1-1.51,3.87-5.9-.65-12.57-1.23-25.15-1.84-37.73-.02-.5,0-1,0-1.78Z"
          />
        </Link>
        <Link
          to="/Counties/538"
          onFocus={(e) => handleFocus(e, "Franklin")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Franklin County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Franklin")}
            className={
              activeCounty === "Franklin"
                ? "active-county-section"
                : "county-section"
            }
            d="M502.56,901.7c-.19-5.06-.46-9.86-.48-14.67,0-1.35,.54-2.83,1.23-4.04,2.74-4.73,2.57-9.5,.61-14.37-.22-.54-.41-1.08-.82-2.15,3.33-.31,6.46-.6,9.56-.88,.7,3.73,1.19,4.13,4.97,4.1,4.37-.03,5.15,.89,4.86-4.88-.25-4.87,.37-9.78,.6-14.67,.06-1.34,.85-1.9,2.2-1.92,3.23-.05,6.46-.08,9.69-.22,.71-.03,1.66-.28,2.05-.76,1.26-1.53,3.26-1.59,4.51-.12,.55,.64,1.43,1.09,2.26,1.4,.66,.24,1.46,.17,2.19,.17,1.51,.01,3.85-.6,4.37,.08,1.82,2.4,3.57,5.06,2.99,8.35-.13,.7-.24,1.41-.46,2.08-2.64,8.07-1.94,16.35-1.7,24.59,.1,3.27,2.37,5.24,5.02,6.8,.53,.31,1.41,.19,2.11,.11,3.1-.33,4.7,.75,5.3,3.7,.15,.76,.23,1.53,.27,2.3,.04,1.02,.01,2.04,.01,3.3-20.31,.56-40.52,1.13-61.37,1.71Z"
          />
        </Link>
        <Link
          to="/Counties/531"
          onFocus={(e) => handleFocus(e, "Canyon")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Canyon County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Canyon")}
            className={
              activeCounty === "Canyon"
                ? "active-county-section"
                : "county-section"
            }
            d="M22.48,662.26c2.06,1.71,3.88,3.45,5.93,4.89,5.19,3.66,10.87,4.44,17.03,2.51,1.39-.44,3-.52,4.46-.36,4.88,.51,9.73,1.29,14.61,1.8,1.93,.2,2.71,.77,2.82,2.77,.22,4.25,.62,8.5,1.31,12.7,.35,2.11,1.57,4.07,2.24,6.15,.61,1.89,1.16,3.82,1.45,5.77,.6,3.95,1.04,7.93,1.5,11.9,.31,2.65-.91,4.53-3.44,5.53-1.41,.55-1.75,1.47-1.94,2.81-.75,5.28-1.66,10.55-2.52,15.88-2.86-.41-4.9-1.33-6.59-3.36-4.83-5.8-9.98-11.35-14.79-17.17-1.5-1.81-2.71-4.05-3.34-6.28-1.28-4.53-3.59-8.35-7.24-11.39-3.96-3.3-8.07-6.45-12.11-9.65-.21-.16-.42-.4-.66-.43-3.19-.45-2.94-2.68-2.86-4.91,.17-4.38,.23-8.77,.37-13.16,.08-2.65,1.28-4.69,3.77-6Z"
          />
        </Link>
        <Link
          to="/Counties/544"
          onFocus={(e) => handleFocus(e, "Jerome")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Jerome County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Jerome")}
            className={
              activeCounty === "Jerome"
                ? "active-county-section"
                : "county-section"
            }
            d="M317.97,808.08c.33,8.51,.65,16.87,.98,25.21-2.63,1.01-5.15,1.88-7.57,2.95-2.15,.95-4.23,2.08-6.29,3.22-2.65,1.47-5.3,1.84-8,.22-.85-.51-1.75-1.04-2.42-1.74-4.28-4.45-9.87-6.48-15.69-8.3-8.62-2.7-17.14-5.71-25.71-8.56-1.08-.36-1.61-.82-1.49-2.08,.59-6.36,1.05-12.74,1.57-19.11,.04-.45,.09-.9,.21-1.34,.91-3.21,1.22-3.43,4.65-3.32,4.71,.14,9.42,.27,14.13,.43,3.01,.1,5.62,.7,6.33,4.17,.12,.58,1.19,1.16,1.94,1.41,3.74,1.25,7.58,.7,11.4,.48,3.96-.23,7.93-.38,11.9-.44,2.86-.05,3.63,.66,4.17,3.36,.27,1.39,1.16,2.1,2.6,2.31,2.39,.34,4.77,.74,7.3,1.13Z"
          />
        </Link>
        <Link
          to="/Counties/540"
          onFocus={(e) => handleFocus(e, "Gem")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Gem County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Gem")}
            className={
              activeCounty === "Gem"
                ? "active-county-section"
                : "county-section"
            }
            d="M70.59,637.26c1.47-.09,2.68-.25,3.89-.22,1.39,.03,1.94-.6,2.05-1.83,.22-2.31,.49-4.62,.71-6.94,.13-1.37,.77-2.23,2.21-2.62,2.92-.78,4.48-2.77,4.88-5.57,1.09-7.54,2.09-15.09,3.2-22.63,.42-2.87,1.01-5.71,1.62-8.55,.54-2.54,2.07-4.34,4.76-4.99,2.5-.61,4.1-2.01,4.97-4.34,.34-.91,1.12-1.66,1.69-2.48,.26,.09,.52,.18,.79,.27,.26,7.24,.6,14.49,.67,21.73,0,1-1.12,2.4-2.11,2.94-2.49,1.34-3.57,3.26-3.53,5.85,.07,4.26,.19,8.52,.4,12.77,.07,1.39-.29,2.29-1.68,2.9-3.82,1.69-5.18,4.68-5.14,8.63,.1,10.66,.04,21.31-.04,31.97-.01,1.39-.49,2.78-.8,4.4-2.99,0-5.88,.14-8.75-.04-3.8-.24-7.45,.09-11.19,.93-2.36,.53-5.04-.05-7.55-.31-3.81-.41-7.6-1.01-11.4-1.47-1.16-.14-1.59-.58-1.56-1.73,.17-5.29,.24-10.58,.41-15.87,.06-1.95,1.15-2.89,3.19-2.9,3.44-.01,6.87,.05,10.3-.05,.66-.02,1.3-.7,1.96-1.05,.64-.34,1.32-.94,1.95-.91,2.05,.1,2.91-.78,3.13-2.65,.2-1.72,.63-3.41,.98-5.24Z"
          />
        </Link>
        <Link
          to="/Counties/548"
          onFocus={(e) => handleFocus(e, "Lewis")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Lewis County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Lewis")}
            className={
              activeCounty === "Lewis"
                ? "active-county-section"
                : "county-section"
            }
            d="M122.57,358.77c-2.6-.48-5.22-.92-7.81-1.46-4.03-.83-7.7,1.7-8.31,5.6-.09,.6-1.07,1.54-1.64,1.54-3.55,0-7.25,.42-9.96-2.73-1.67-1.94-4.06-2.79-6.67-2.68-2.14,.09-4.39,.16-6.37,.83-1.15,.39-2.21,1.83-2.69,3.02-.62,1.52-.67,3.27-.86,4.94-.25,2.23-1.22,3.83-3.47,4.81-.7,.3-1.36,1.42-1.41,2.21-.15,2.31,0,4.65,.08,6.97,.05,1.39-.55,1.84-1.98,1.57-2.02-.38-3.67-.97-4.9-2.85-1.69-2.56-3.94-2.23-5.08,.47q-1.19,2.81-4.65,2.19c-.74-9.23-2.44-18.47-1.21-27.75,.5-3.78-.04-6.99-2.22-10.05-.29-.4-.44-.9-.76-1.57,5.39,.25,10.54,.46,15.69,.73,2.63,.14,5.11,.05,7.61-1.14,2.75-1.3,5.74-1.85,8.67,.01,.43,.28,1.58,0,2.09-.4,2.67-2.08,3.01-5.83,.89-8.42-.91-1.12-2-2.16-2.64-3.4-.35-.67-.19-1.85,.21-2.54,.63-1.08,1.91-1.1,2.79-.3,2.23,2.03,4.43,4.12,6.45,6.34,.98,1.08,1.9,1.7,3.41,1.56,1.14-.1,2.31,.11,3.43-.04,3.74-.49,6.76,.57,9.37,3.24,1.04,1.07,1.78,1.95,1.6,3.45-.13,1.02-.03,2.07-.09,3.1-.26,4.12,1.69,6.88,5.52,8.61,1.27,.57,2.5,1.27,3.66,2.03,.61,.4,1.03,1.06,1.53,1.6l-.27,.49Z"
          />
        </Link>
        <Link
          to="/Counties/558"
          onFocus={(e) => handleFocus(e, "Teton")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Teton County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Teton")}
            className={
              activeCounty === "Teton"
                ? "active-county-section"
                : "county-section"
            }
            d="M598.36,700.5c-3.79-.39-7.29-.55-10.67-1.23-1.06-.21-2.21-1.74-2.61-2.89-2.25-6.44-4.81-7.17-11.22-7.02-2.68,.06-5.37,.01-8.33,.01,1.53-12.2-.01-24.17-1.28-36.44,10.44-3.28,21.69-3.6,32.09-9.14,.48,3.84,1.14,7.04,1.23,10.26,.25,9.49,.25,18.99,.4,28.48,.08,4.84,.26,9.68,.39,14.52,.03,1.02,0,2.03,0,3.46Z"
          />
        </Link>
        <Link
          to="/Counties/550"
          onFocus={(e) => handleFocus(e, "Madison")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Madison County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Madison")}
            className={
              activeCounty === "Madison"
                ? "active-county-section"
                : "county-section"
            }
            d="M509.7,653.23c3.53,.71,6.75,1.06,9.76,2.02,7.64,2.45,15.49,2.76,23.37,2.18,2.59-.19,5.1-1.54,7.64-2.38,3.44-1.15,6.87-2.31,10.62-1.8,1.01,.14,1.58,.42,1.64,1.51,.53,9.21,1.15,18.42,1.61,27.64,.11,2.23-.31,4.49-.51,7.01h-20.5c0-1.41,.07-3-.02-4.58-.12-2.37-.55-2.64-2.95-2.11-3.07,.68-5.99,.09-8.5-1.55-3-1.95-5.96-4.03-8.58-6.42-2.73-2.49-5.45-2.6-8.65-1.22-3.92,1.68-4.55,1.56-4.84-2.6-.4-5.64-.1-11.32-.1-17.69Z"
          />
        </Link>
        <Link
          to="/Counties/555"
          onFocus={(e) => handleFocus(e, "Payette")}
          onBlur={() => setHoveredCounty("")}
          aria-label="Payette County"
          tabIndex={tabIndex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.click();
            }
          }}
        >
          <path
            onMouseLeave={() => setHoveredCounty("")}
            onMouseOver={() => setHoveredCounty("Payette")}
            className={
              activeCounty === "Payette"
                ? "active-county-section"
                : "county-section"
            }
            d="M75.62,626.83c-.26,2.61-.52,4.91-.69,7.22-.08,1.06-.58,1.4-1.66,1.43-3.99,.1-4.13-.72-4.87,4.04-.2,1.26-.52,2.51-.78,3.8-1.89,.14-3.82-.04-4.81,1.95-.13,.26-.96,.26-1.47,.27-3.16,.02-6.33,0-9.49,.01-2.42,0-4.27,1.47-4.38,3.83-.24,5.28-.4,10.57-.37,15.86,0,1.69-.64,2.34-2.18,2.83-7.14,2.29-12.97,.13-18.34-4.46-2.53-2.16-3.36-4.2-2.44-7.54,1.26-4.58,1.77-9.37,2.26-14.1,.17-1.7-.48-3.56-1.09-5.23-.54-1.48-.6-2.79,.35-4.02,1.84-2.38,3.67-4.79,5.7-7.02,.6-.66,1.89-1.06,2.85-1.04,10.9,.18,21.8,.38,32.69,.78,2.86,.1,5.69,.89,8.76,1.4Z"
          />
        </Link>
      </svg>
    </>
  );
};

export default IdahoCountiesMap;
